import { Head, router } from '@inertiajs/react'
import { useMemo, useState } from 'react'
import AppLayout from '@/components/AppLayout.tsx'
import PlanTaskWidget, { stepsDone as stepsDoneCount, type TaskWidget } from './components/PlanTaskWidget'
import RevisionSubmitWidget from './components/RevisionSubmitWidget'
import {
  plan_top_down_path,
  plan_top_down_submit_path,
  plan_top_down_review_path,
  plan_top_down_complete_overall_path,
} from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'
import StepProgress from './components/StepProgress'
import EditorHeader, { DraftActivityBanner, RevisionBanner } from './components/EditorHeader'
import EditToolbar from './components/EditToolbar'
import NodeSearchPanel, { type NodeRevenueData, type NodeRevenueEntry } from './components/NodeSearchPanel'
import PlanNodesPanel, { type PlanNode } from './components/PlanNodesPanel'
import { applyAddNode, applyRemoveNode, applyTargetEdit, syncSpecialAtLevel } from './components/targetReconcile'
import { uuidv7 } from '@/lib/uuidv7'
import { type PlanSwitcherData } from './components/PlanSwitcher'
import EditPlanModal from './components/EditPlanModal'
import { useNodesAutosave } from './hooks/useNodesAutosave'
import { findNode, findPath, usePickerDrill } from './hooks/usePickerDrill'
import RevisionRequestBanner from './components/RevisionRequestBanner'
import { type EditorRequest, type RequestComment } from './components/revisionRequest'
import { useRowNotes } from './components/useRowNotes'
import { revisionLabels } from './components/periodLabel'

interface Props {
  slug: string
  begin_date: string
  end_date: string
  top_down_plan: {
    id: string
    name: string
    slug: string
    revenue_target: number
    currency: string | null
    overall_completed_at: string | null
    channel_step_completed_at: string | null
    quarterly_step_completed_at: string | null
    org_node: { id: string; name: string; slug: string } | null
  }
  period: { start: string; end: string }
  // The plan period a revision's numbers cover (target months only); the
  // months before it are actual sales. Present for every version, but only
  // meaningful (narrower than begin_date–end_date) for a revision.
  plan_period: { start: string; end: string }
  // Number of months at the start of the plan already covered by actual
  // sales. Null for a plain version.
  actuals_months: number | null
  node_revenue: NodeRevenueData
  plan_nodes: PlanNode[]
  // The draft this page edits; its id rides along with every autosave.
  version: { id: string } | null
  // The draft is a revision of an already-published plan.
  revision: boolean
  // Someone else's unsubmitted edits in the shared draft (server omits it
  // when the viewer made the last edit).
  draft_activity?: { by: string; at: string } | null
  plan_switcher: PlanSwitcherData
  // Every open planning task the viewer is a PIC of (Plan::CycleTaskProps); empty for everyone else.
  task_widgets: TaskWidget[]
  // Published plan with no planning task open: the Submit-for-review control.
  revision_submit: boolean
  // The open sent request on this plan, this page's comments only. Null
  // when there is no open request.
  revision_request: EditorRequest | null
  review_comments?: RequestComment[]
  // Nodes the published plan already has. A quarterly revision keeps them
  // (their actual months are already sold), so the editor offers no Remove
  // on those rows. Empty outside a revision.
  published_node_ids: string[]
}

export default function Edit({
  slug,
  begin_date,
  end_date,
  top_down_plan,
  period,
  plan_period,
  actuals_months,
  node_revenue,
  plan_nodes,
  version,
  revision,
  draft_activity,
  plan_switcher,
  task_widgets,
  revision_submit,
  revision_request,
  review_comments,
  published_node_ids,
}: Props) {
  const market = useMarket()
  const [planNodes, setPlanNodes] = useNodesAutosave({
    marketSlug: market.slug, slug, serverNodes: plan_nodes, versionId: version?.id ?? null,
  })
  const [showEditPlan, setShowEditPlan] = useState(false)
  const publishedNodeIds = useMemo(() => new Set(published_node_ids), [published_node_ids])
  const [overallCompletedAt, setOverallCompletedAt] = useState<string | null>(top_down_plan.overall_completed_at)
  const { drillNodeId, handleDrill, handleNavigate, handleClearDrill, resetDrill } = usePickerDrill({
    marketSlug: market.slug, slug, planNodes,
  })

  const drillNode = drillNodeId ? findNode(planNodes, drillNodeId) : null
  const drillPath = drillNodeId ? findPath(planNodes, drillNodeId) ?? [] : []

  const rowNotes = useRowNotes(revision_request, review_comments)

  const planTarget = useMemo(
    () => planNodes.reduce((sum, n) => sum + (n.targetRevenue ?? Number(n.revenue) ?? 0), 0),
    [planNodes]
  )
  // Month labels for a revision's actual-vs-plan split; null for a plain
  // version, so every column that shows one carries its months.
  const periodLabels = useMemo(
    () => revisionLabels(plan_period, actuals_months, begin_date, period),
    [plan_period, actuals_months, begin_date, period]
  )
  const actualTotal = useMemo(
    () => planNodes.reduce((sum, n) => sum + Number(n.actualRevenue ?? 0), 0),
    [planNodes]
  )
  const parentTarget = drillNode
    ? (drillNode.targetRevenue ?? Number(drillNode.revenue) ?? 0)
    : planTarget
  const breadcrumb = useMemo(() => {
    const crumbs = [{ id: null as string | null, label: 'Top level' }]
    drillPath.forEach((n) => crumbs.push({ id: n.id, label: n.name }))
    return crumbs
  }, [drillPath])

  const levelRows = drillNode ? drillNode.children : planNodes
  const addedKeys = useMemo(
    () => new Set(levelRows.filter((n) => n.productNodeId).map((n) => n.productNodeId as string)),
    [levelRows]
  )
  const specialAdded = levelRows.some((n) => n.special)
  const newProductAdded = useMemo(() => planNodes.some((n) => n.isNewProduct), [planNodes])

  const syncSpecialForCurrentLevel = (nodes: PlanNode[]): PlanNode[] => {
    if (drillNode) {
      return syncSpecialAtLevel(nodes, drillNode.id, Number(drillNode.revenue) || 0, drillNode.share)
    }
    return syncSpecialAtLevel(nodes, null, Number(node_revenue.total_revenue) || 0, 1)
  }

  const handleAdd = (entry: NodeRevenueEntry) => {
    if (addedKeys.has(entry.product_node_id)) return
    const newNode: PlanNode = {
      id: uuidv7(),
      name: entry.name,
      revenue: entry.revenue,
      share: entry.share,
      targetRevenue: Number(entry.revenue) || 0,
      productNodeId: entry.product_node_id,
      productNodeHasChildren: entry.has_children,
      children: []
    }
    setPlanNodes((prev) => syncSpecialForCurrentLevel(applyAddNode(prev, drillNode?.id ?? null, newNode)))
  }

  const handleAddSpecial = () => {
    if (!drillNode) {
      if (planNodes.some((n) => n.special)) return
      const siblingShareSum = planNodes.reduce((sum, n) => sum + ((n.special || n.isNewProduct) ? 0 : n.share), 0)
      const otherShare = Math.max(1 - siblingShareSum, 0)
      const total = Number(node_revenue.total_revenue) || 0
      const siblingRevenueSum = planNodes.reduce(
        (sum, n) => sum + ((n.special || n.isNewProduct) ? 0 : Number(n.revenue) || 0),
        0
      )
      const otherRevenue = Math.max(total - siblingRevenueSum, 0)
      const newNode: PlanNode = {
        id: uuidv7(),
        name: 'Other',
        revenue: String(otherRevenue),
        share: otherShare,
        special: true,
        targetRevenue: otherRevenue,
        growthPct: 0,
        children: []
      }
      setPlanNodes((prev) => applyAddNode(prev, null, newNode))
      return
    }
    if (drillNode.children.some((c) => c.special)) return
    const siblingShareSum = drillNode.children.reduce((sum, n) => sum + ((n.special || n.isNewProduct) ? 0 : n.share), 0)
    const otherShare = Math.max(drillNode.share - siblingShareSum, 0)
    const parentRevenue = Number(drillNode.revenue) || 0
    const siblingRevenueSum = drillNode.children.reduce(
      (sum, n) => sum + ((n.special || n.isNewProduct) ? 0 : Number(n.revenue) || 0),
      0
    )
    const otherRevenue = Math.max(parentRevenue - siblingRevenueSum, 0)
    const newNode: PlanNode = {
      id: uuidv7(),
      name: 'Other',
      revenue: String(otherRevenue),
      share: otherShare,
      special: true,
      targetRevenue: otherRevenue,
      growthPct: 0,
      children: []
    }
    setPlanNodes((prev) => applyAddNode(prev, drillNode.id, newNode))
  }

  const handleAddNewProduct = () => {
    if (drillNode) return
    if (planNodes.some((n) => n.isNewProduct)) return
    const newNode: PlanNode = {
      id: uuidv7(),
      name: 'New Products',
      revenue: '0',
      share: 0,
      special: false,
      isNewProduct: true,
      targetRevenue: 0,
      growthPct: 0,
      children: []
    }
    setPlanNodes((prev) => applyAddNode(prev, null, newNode))
  }

  const handleTargetChange = (
    id: string,
    target: number | null,
    growthPctOverride?: number | null
  ) => {
    setPlanNodes((prev) => applyTargetEdit(prev, id, target, { editedGrowthPct: growthPctOverride }))
  }

  const handleRemove = (id: string) => {
    const removedWasDrill = drillNodeId === id
    if (removedWasDrill) resetDrill()
    setPlanNodes((prev) => {
      const next = applyRemoveNode(prev, id)
      if (removedWasDrill) return next
      return syncSpecialForCurrentLevel(next)
    })
  }

  const handleCompleteOverall = async () => {
    const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
    const res = await fetch(plan_top_down_complete_overall_path(market.slug, slug), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrf,
        Accept: 'application/json'
      },
      credentials: 'same-origin'
    })
    if (res.ok) {
      const data = await res.json()
      setOverallCompletedAt(data.overall_completed_at)
    } else {
      const data = await res.json().catch(() => ({ error: 'Failed to complete overall plan.' }))
      window.alert(data.error ?? 'Failed to complete overall plan.')
    }
  }

  const done = stepsDoneCount(overallCompletedAt, top_down_plan.channel_step_completed_at, top_down_plan.quarterly_step_completed_at)

  return (
    <AppLayout
      header={<EditorHeader
        name={top_down_plan.name}
        beginDate={begin_date}
        endDate={end_date}
        orgNodeName={top_down_plan.org_node?.name ?? null}
        revenueTarget={planTarget}
        currency={top_down_plan.currency}
        onEditPlan={() => setShowEditPlan(true)}
        planPeriod={plan_period}
        actualsMonths={actuals_months}
        fullYearTarget={periodLabels ? actualTotal + planTarget : null}
      />}
      headerActions={
        task_widgets.length > 0
          ? (
            <div className="flex flex-col gap-1.5">
              {task_widgets.map((w) => (
                <PlanTaskWidget
                  key={w.id}
                  task={w}
                  done={done}
                  submitHref={plan_top_down_submit_path(market.slug, slug, { task_id: w.id })}
                  reviewHref={plan_top_down_review_path(market.slug, slug, { task_id: w.id })}
                />
              ))}
            </div>
          )
          : revision_submit && <RevisionSubmitWidget done={done} submitHref={plan_top_down_submit_path(market.slug, slug)} />
      }
    >
      <Head title={top_down_plan.name} />
      <EditToolbar
        slug={slug}
        planId={top_down_plan.id}
        beginDate={begin_date}
        endDate={end_date}
        period={period}
        planSwitcher={plan_switcher}
        periodLabels={periodLabels}
      />
      {showEditPlan && (
        <EditPlanModal
          plan={{
            name: top_down_plan.name,
            slug: top_down_plan.slug,
            begin_date: begin_date,
            end_date: end_date,
            org_node_id: top_down_plan.org_node?.id ?? null,
          }}
          orgNodes={plan_switcher.org_nodes}
          onClose={() => setShowEditPlan(false)}
          onSaved={(newSlug) => router.visit(plan_top_down_path(market.slug, newSlug))}
        />
      )}
      <StepProgress
        slug={slug}
        currentStep="overall"
        overallCompletedAt={overallCompletedAt}
        channelCompletedAt={top_down_plan.channel_step_completed_at}
        quarterlyCompletedAt={top_down_plan.quarterly_step_completed_at}
        onCompleteOverall={handleCompleteOverall}
      />
      {revision && <RevisionBanner />}
      {revision_request && <RevisionRequestBanner request={revision_request} section="overall" slug={slug} />}
      {draft_activity && <DraftActivityBanner activity={draft_activity} />}
      <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 2fr' }}>
        <NodeSearchPanel
          data={node_revenue}
          addedKeys={addedKeys}
          currency={top_down_plan.currency}
          specialAdded={specialAdded}
          newProductAdded={newProductAdded}
          onAdd={handleAdd}
          onAddSpecial={handleAddSpecial}
          onAddNewProduct={handleAddNewProduct}
          onClearDrill={handleClearDrill}
        />
        <PlanNodesPanel
          nodes={planNodes}
          drillNodeId={drillNodeId}
          parentTarget={parentTarget}
          topLevelTotal={periodLabels ? planTarget + actualTotal : planTarget}
          currency={top_down_plan.currency}
          breadcrumb={breadcrumb}
          onRemove={handleRemove}
          onDrill={handleDrill}
          onNavigate={handleNavigate}
          onTargetChange={handleTargetChange}
          rowNotes={rowNotes}
          revision={periodLabels}
          publishedNodeIds={publishedNodeIds}
        />
      </div>
    </AppLayout>
  )
}
